import {Component, EventEmitter, Input, Output} from '@angular/core';
import {NgClass, NgForOf, NgIf} from "@angular/common";
import { distinctUntilChanged, filter, Subscription, take } from "rxjs";
import { selectAllTags, selectTagsError, selectTagsLoading } from "../../../state/tag/tag.selectors";
import { Store } from "@ngrx/store";
import { AppState } from "../../../state/app.state";
import { SharedService } from "../../../services/shared.service";
import * as TagActions from '../../../state/tag/tag.actions';
import { MatTooltip } from "@angular/material/tooltip";
import { ReportsSortComponent, SortOption, DEFAULT_SORT } from "../reports-sort/reports-sort.component";
import { ReportsDateRangeComponent, DateRange, DEFAULT_DATE_RANGE } from "../reports-date-range/reports-date-range.component";

@Component({
  selector: 'app-reports-filter',
  standalone: true,
  imports: [
    NgForOf,
    NgIf,
    NgClass,
    MatTooltip,
    ReportsSortComponent,
    ReportsDateRangeComponent
  ],
  templateUrl: './reports-filter.component.html',
  styleUrl: './reports-filter.component.scss'
})
export class ReportsFilterComponent {

  @Input() exporting: boolean = false;
  @Input() disableExport: boolean = false;
  @Input() selectedCount: number = 0;
  @Input() sort: SortOption = DEFAULT_SORT;
  @Input() dateRange: DateRange = DEFAULT_DATE_RANGE;
  @Output() filtersChanged = new EventEmitter<{ [key: string]: string[] } | null>();
  @Output() exportClicked = new EventEmitter<void>();
  @Output() sortChanged = new EventEmitter<SortOption>();
  @Output() dateRangeChanged = new EventEmitter<DateRange>();

  filtersOpen: boolean = false;
  sortOpen: boolean = false;

  selectedFiltersCount: number = 0;

  tagsSubscription: Subscription;
  errorSubscription: Subscription;
  loadingSubscription: Subscription;

  tags: any = {};  // Placeholder for tags data

  filterOptions = {
    roles: [],
    dealStages: [],
    dealValues: [],
    statuses: [],
    closeDates: [],
    myJudgement: [],
    icpName: []
  };

  // Initialize selectedFilters to null
  selectedFilters: { [key: string]: string[] } | null = null;

  constructor(private store: Store<AppState>) {}

  ngOnInit(): void {
    this.getTags();
  }

  ngOnDestroy(): void {
    if (this.tagsSubscription) {
      this.tagsSubscription.unsubscribe();
    }
    if (this.errorSubscription) {
      this.errorSubscription.unsubscribe();
    }
    if (this.loadingSubscription) {
      this.loadingSubscription.unsubscribe();
    }
  }

  /**
   * Get all tags.
   */
  getTags(): void {
    // console.log('getTags started');

    // Dispatch the action to load tags
    this.store.dispatch(TagActions.loadTags());

    // Subscribe to the loading state to ensure tags are loaded before accessing them
    this.loadingSubscription = this.store.select(selectTagsLoading)
      .pipe(
        filter(loading => !loading),  // Only proceed when loading becomes false
        take(1)  // Only take the first loading completion event
      )
      .subscribe(() => {
        // console.log('Tags loading completed');

        // Now subscribe to the tags once loading is completed
        this.tagsSubscription = this.store.select(selectAllTags)
          .pipe(
            distinctUntilChanged(),  // Only emit when the tags object changes
            take(1)  // Take the first change (the updated object)
          )
          .subscribe((tags: any) => {
            // console.log('Tags loaded successfully:', tags);

            // Ensure tags is an object and has the expected properties
            if (tags && typeof tags === 'object') {
              // Process tags to generate filter options
              this.filterOptions = {
                roles: this.mapTagOptions(tags.roles || []),
                dealStages: this.mapTagOptions(tags.dealStages || []),
                dealValues: this.mapTagOptions(tags.dealValues || []),
                statuses: this.mapTagOptions(tags.statuses || []),
                closeDates: this.mapTagOptions(tags.closeDates || []),
                myJudgement: this.mapTagOptions(tags.myJudgement || []),
                icpName: this.mapTagOptions(tags.icpName || [])
              };
              // console.log('Filter options:', this.filterOptions);
            }
          });
      });

    // Subscribe to tag loading errors
    this.errorSubscription = this.store.select(selectTagsError)
      .pipe(filter(error => !!error), take(1))
      .subscribe(error => {
        console.error('Failed to load tags:', error);
        // Handle error (e.g., show an error message)
      });
  }

  /**
   * Map tag options for filter display.
   */
  mapTagOptions(tags: any[]): { label: string, value: string }[] {
    return tags.map(tag => ({
      label: tag.name?.en || '',  // Ensure label exists
      value: tag.slug?.en || ''   // Ensure value exists
    }));
  }

  toggleFilters(): void {
    this.filtersOpen = !this.filtersOpen;
  }

  toggleSort(): void {
    this.sortOpen = !this.sortOpen;
  }

  // Update selected filters on change
  onFilterChange(category: string, value: string, event: any): void {
    // Initialize selectedFilters if it's null
    if (this.selectedFilters === null) {
      this.selectedFilters = {};
    }

    // Create a shallow copy of selectedFilters to avoid direct mutation
    this.selectedFilters = { ...this.selectedFilters };

    // Create a copy of the category array to avoid direct mutation
    const updatedCategory = [...(this.selectedFilters[category] || [])];

    if (event.target.checked) {
      updatedCategory.push(value);
    } else {
      // Remove the value from the array
      const index = updatedCategory.indexOf(value);
      if (index > -1) {
        updatedCategory.splice(index, 1); // Remove the value using splice
      }
    }

    // If there are still values in the category, update the category in selectedFilters
    if (updatedCategory.length > 0) {
      this.selectedFilters[category] = updatedCategory;
    } else {
      // If no values remain in the category, delete the category from selectedFilters
      delete this.selectedFilters[category];
    }

    // If selectedFilters is empty, set it to null
    if (Object.keys(this.selectedFilters).length === 0) {
      this.selectedFilters = null;
    }

    // console.log('[dashboard-filter] onFilterChange', this.selectedFilters);

    // Emit the updated filters
    this.filtersChanged.emit(this.selectedFilters);
  }


  // Clear all filters
  clearAllFilters(): void {
    this.selectedFilters = null;
    this.filtersChanged.emit(this.selectedFilters);
    this.filtersOpen = false
  }

  // Remove a specific filter and deselect the checkbox
  removeFilter(category: string, value: string): void {
    if (this.selectedFilters && this.selectedFilters[category]) {
      // Create a shallow copy of the selectedFilters to avoid direct mutation
      this.selectedFilters = { ...this.selectedFilters };

      // Create a copy of the category array and remove the value
      const updatedCategory = this.selectedFilters[category].filter(v => v !== value);

      if (updatedCategory.length > 0) {
        // Update the category in selectedFilters
        this.selectedFilters[category] = updatedCategory;
      } else {
        // Remove the category from selectedFilters if it's empty
        delete this.selectedFilters[category];
      }

      // If selectedFilters is empty, set it to null
      if (Object.keys(this.selectedFilters).length === 0) {
        this.selectedFilters = null;
      }

      // Emit the updated filters
      this.filtersChanged.emit(this.selectedFilters);
    }
  }


  // Get active filters as a list with category information
  getActiveFilters(): { category: string, value: string, label: string }[] {
    let activeFilters: { category: string, value: string, label: string }[] = [];
    if (this.selectedFilters) {
      for (let category in this.selectedFilters) {
        const options = this.filterOptions[category];
        const labels = this.selectedFilters[category].map(value => {
          const option = options.find(o => o.value === value);
          return option ? { category, value, label: option.label } : null;
        }).filter(item => item !== null);
        activeFilters = activeFilters.concat(labels);
      }
    }
    return activeFilters;
  }

  // Check if a filter is selected (for binding checkbox states)
  isSelected(category: string, value: string): boolean {
    return this.selectedFilters && this.selectedFilters[category]?.includes(value);
  }

  onExport(): void {
    this.exportClicked.emit();
  }

  onSortChange(sort: SortOption): void {
    this.sort = sort;
    this.sortChanged.emit(sort);
  }

  onDateRangeChange(range: DateRange): void {
    this.dateRange = range;
    this.dateRangeChanged.emit(range);
  }

}
